import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FloatingBall, Pagination, Sticky } from 'react-vant';
import { apiGetGoodsList, apiGetH5Config, apiGetProvinceByIp, apiGetRecommendedGoods } from '../api';
import Empty from '../components/empty';
import GoodsItem from '../components/goods-item';
import GoodsTypeTabBar from '../components/goodsTypeTabBar';
import GridGoodsItem from '../components/grid-goods-item';
import Loading from '../components/loading';
import LandingCarousel from '../components/landingCarousel';
import AutoScrollNotice from '../components/autoScrollNotice';
import Page from '../components/page';
import PopularityItem from '../components/popularity-item';
import ScreenDialog from '../components/screenDialog';
import TabBar from '../components/tabBar';
import main from '../main';
import { Goods, H5ConfigJson, SelectOption } from '../types';
import { buildVisibleGoodsTypeOptions, readLandingSearchParams } from '../utils/landingPage';
import { parseLocationParams } from '../utils/utils';
import ProvinceDialog from '../components/provinceDialog';

enum OperatorsType {
  /** 推荐 */
  all = 'all',
  /** 中国广电 */
  cbn = 'cbn',
  /** 中国电信 */
  telecom = 'telecom',
  /** 中国联通 */
  unicom = 'unicom',
  /** 中国移动 */
  cmcc = 'cmcc',
  /** 本地资费 */
  local = 'local',
}

const mobileNetOptions: Array<SelectOption> = [
  { label: '全部', value: OperatorsType.all },
  { label: '中国移动', value: OperatorsType.cmcc },
  { label: '中国联通', value: OperatorsType.unicom },
  { label: '中国电信', value: OperatorsType.telecom },
  { label: '中国广电', value: OperatorsType.cbn },
  { label: '本地资费', value: OperatorsType.local },
];
export default function HomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  let searchParams = readLandingSearchParams();
  const [errorText, seterrorText] = useState<string>();
  // 默认样式 0:单行 1:双列
  let [styleLine, setStyleLine] = useState('0');
  // 运营商
  const [mobileNet, setmobileNet] = useState(searchParams?.mobileNet ?? mobileNetOptions[0]?.value.toString());
  const [goodsTypeOptions, setgoodsTypeOptions] = useState<SelectOption[]>([]);
  // 类型
  const [goodsType, setgoodsType] = useState<string>(searchParams?.goodsType ?? '');
  const [pageNum, setpageNum] = useState<number>(searchParams?.pageNum ?? 1);
  const [total, settotal] = useState<number>();
  const [currentProvince, setcurrentProvince] = useState('');
  const [activeProvince, setactiveProvince] = useState(searchParams?.receiverAddress ?? '');
  const [goodsName, setGoodsName] = useState<string>();

  const [screenDialog, setScreenDialog] = useState<boolean>(false);
  const [provinceDialogShow, setprovinceDialogShow] = useState<boolean>(false);

  const [goodsList, setGoodsList] = useState<Array<Goods>>([]);
  // 人气推荐数据列表
  const [recommendedGoods, setRecommendedGoodsList] = useState<Array<Goods>>([]);
  const [h5Config, setH5Config] = useState<H5ConfigJson>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const landingRequestIdRef = useRef(0);
  const goodsRequestIdRef = useRef(0);
  const recommendRequestIdRef = useRef(0);
  const locationParams = parseLocationParams(location.search) ?? {};
  const landingCode = String(locationParams.code ?? main.shopId ?? '').trim();

  const lastReportedHeightRef = useRef<number>();
  const notifyIframeHeight = useCallback(() => {
    if (!scrollContainerRef.current) return;
    window.requestAnimationFrame(() => {
      const realHeight = scrollContainerRef.current?.scrollHeight || 0;
      const nextHeight = realHeight + 60;
      if (realHeight <= 0 || lastReportedHeightRef.current === nextHeight) return;
      lastReportedHeightRef.current = nextHeight;
      window.parent.postMessage({ type: 'iframeHeight', height: nextHeight }, '*');
    });
  }, []);
  useEffect(() => {
    notifyIframeHeight();
  }, [goodsList, recommendedGoods, h5Config.bottomImgList, h5Config.bannerImgList, styleLine, errorText, total]);
  useEffect(() => {
    let { uc } = locationParams;
    if (uc) {
      main.uc = uc;
      sessionStorage.setItem('uc', uc);
    }
  }, [location.search]); // eslint-disable-line

  useEffect(() => {
    const requestId = ++landingRequestIdRef.current;
    if (landingCode) main.setShopId(landingCode);
    const expectedCode = main.shopId;
    const storedSearchParams = readLandingSearchParams();

    // 切换落地页时立即作废上一页的异步请求和界面数据，避免 A 页慢请求覆盖 B 页。
    goodsRequestIdRef.current += 1;
    recommendRequestIdRef.current += 1;
    setH5Config({});
    setgoodsTypeOptions([]);
    setGoodsList([]);
    setRecommendedGoodsList([]);
    settotal(undefined);
    seterrorText(undefined);
    setcurrentProvince('');
    setactiveProvince(storedSearchParams.receiverAddress ?? '');
    setgoodsType(storedSearchParams.goodsType ?? '');
    setmobileNet(storedSearchParams.mobileNet ?? mobileNetOptions[0]?.value.toString());
    setpageNum(storedSearchParams.pageNum ?? 1);

    loadH5Config(requestId, expectedCode, storedSearchParams);
    getProvinceByIp(requestId, expectedCode, storedSearchParams.receiverAddress);
    setGoodsName(sessionStorage.getItem('searchIptValue') ?? '');

    return () => {
      if (landingRequestIdRef.current === requestId) landingRequestIdRef.current += 1;
    };
  }, [landingCode]); // eslint-disable-line

  async function getRecommendedGoods(query: any = {}) {
    const requestId = ++recommendRequestIdRef.current;
    try {
      let res = await apiGetRecommendedGoods({
        goodsType: query.goodsType ?? goodsType ?? 'all',
        mobileNet: 'all',
        province: query.province ?? (activeProvince || undefined),
        receiverAddress: query.receiverAddress,
      });
      let data = res.data;
      if (requestId === recommendRequestIdRef.current) {
        setRecommendedGoodsList(data.code === 200 ? (data.data ?? []) : []);
      }
    } catch (e) {
      if (requestId === recommendRequestIdRef.current) setRecommendedGoodsList([]);
    }
  }
  async function loadH5Config(requestId: number, expectedCode: string, storedSearchParams: any) {
    try {
      let res = await apiGetH5Config();
      let data = res.data;
      if (requestId !== landingRequestIdRef.current || main.shopId !== expectedCode) return;
      if (data.code === 200) {
        let _data = data.data || {};
        setH5Config(_data);
        setStyleLine(_data?.styleLine ?? '0');
        main.serviceLink = _data.serviceLink;
        sessionStorage.setItem('serviceLink', _data.serviceLink ?? '');

        const _options = buildVisibleGoodsTypeOptions(_data.goodsTypeOptions);
        setgoodsTypeOptions(_options);
        const preferredGoodsType = storedSearchParams.goodsType ?? '';
        const selectedGoodsType = _options.some((item) => item.value === preferredGoodsType)
          ? preferredGoodsType
          : _options[0]?.value ?? '';
        setgoodsType(selectedGoodsType);
        if (selectedGoodsType) {
          const selectedProvince = storedSearchParams.receiverAddress || undefined;
          const selectedMobileNet = storedSearchParams.mobileNet ?? mobileNetOptions[0]?.value.toString();
          loadGoodsList({
            goodsType: selectedGoodsType,
            mobileNet: selectedMobileNet,
            pageNum: 1,
            province: selectedProvince,
            receiverAddress: selectedProvince,
          });
          getRecommendedGoods({
            goodsType: selectedGoodsType,
            province: selectedProvince,
            receiverAddress: selectedProvince,
          });
        } else {
          setGoodsList([]);
          setRecommendedGoodsList([]);
          settotal(0);
          seterrorText('暂无数据');
        }
      } else {
        seterrorText(data?.msg ?? '页面配置加载失败');
      }
    } catch (e) {
      if (requestId === landingRequestIdRef.current && main.shopId === expectedCode) {
        seterrorText('页面配置加载失败，请稍后重试');
      }
    }
  }
  async function getProvinceByIp(requestId: number, expectedCode: string, selectedProvince?: string) {
    try {
      let res = await apiGetProvinceByIp();
      let data = res.data;
      if (requestId !== landingRequestIdRef.current || main.shopId !== expectedCode) return;
      if (data.code === 200) {
        const provinceMessage = String(data.msg ?? '').trim();
        if (!provinceMessage) return;
        const detectedProvince = provinceMessage.includes('黑龙江') ? '黑龙江' : provinceMessage.substring(0, 2);
        setcurrentProvince(detectedProvince);
        setactiveProvince(selectedProvince || detectedProvince);
      }
    } catch (e) { }
  }
  function handleChangePageNum(num: number) {
    setpageNum(num);
    let element = document.getElementById('contentBlock');
    if (element) {
      element.scrollIntoView();
    }
    pageNum !== num && loadGoodsList({ pageNum: num });
  }
  async function loadGoodsList(query?: any) {
    const requestId = ++goodsRequestIdRef.current;
    seterrorText('');
    let searchParams = readLandingSearchParams();
    let param = {
      ...searchParams,
      ...query,
    };
    sessionStorage.setItem('searchParams', JSON.stringify(param));
    setGoodsList([]);
    try {
      let res = await apiGetGoodsList({
        goodsType,
        mobileNet: mobileNet ?? 'all',
        pageNum,
        province: activeProvince || undefined,
        ...searchParams,
        ...query,
        pageSize: 12,
        recommendedGoods: 'Y',
        goodsName: sessionStorage.getItem('searchIptValue') ?? '',
      });
      let data = res.data;
      if (requestId !== goodsRequestIdRef.current) return;
      if (data.code === 200) {
        const rows = data.rows ?? [];
        setGoodsList(rows);
        settotal(data.total ?? 0);
        if (rows.length === 0) {
          seterrorText('暂无数据');
        }
      } else {
        seterrorText(data?.msg ?? '暂无数据');
      }
    } catch (e: any) {
      if (requestId === goodsRequestIdRef.current) seterrorText('商品加载失败，请稍后重试');
    }
  }
  // 筛选
  async function handleConfirmSearch() {
    let _goodsName = sessionStorage.getItem('searchIptValue') ?? '';
    let searchParams = readLandingSearchParams();
    setgoodsType(searchParams?.goodsType);
    setmobileNet(searchParams?.mobileNet);
    setactiveProvince(searchParams?.receiverAddress ?? '');
    setGoodsName(_goodsName);
    setpageNum(searchParams?.pageNum);
    setScreenDialog(false);
    setprovinceDialogShow(false);
    loadGoodsList(searchParams);
    getRecommendedGoods(searchParams);
  }
  // 返回顶部
  function scrollToTop() {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth', // 平滑动画
      });
    }
  }
  let list = goodsList || [];

  let bannerList = (h5Config.bannerImgList ?? []).filter((i) => !!i?.label);
  // 流量卡对应运营商
  let IotCardOptions = mobileNetOptions.filter((item) => item.value !== 'local');
  let goodsTypeData = goodsTypeOptions ?? [];
  // 列表样式转换 0:不允许 1:允许
  let styleTrans = h5Config?.styleTrans ?? '0';
  return (
    <Page
      className="home-page d-flex-c"
      contentClassName="flex-1 overflow-y"
      contentRef={scrollContainerRef}
      navPage
      style={{
        background: 'url("/pic/pic_page_bg_new.png")',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#f1f1f1',
      }}
    >
      <div className="banner-block">
        <LandingCarousel images={bannerList} onImageReady={notifyIframeHeight} />
        {h5Config.tipsMsg && (
          <AutoScrollNotice
            text={h5Config.tipsMsg}
            className={`tips-block ${bannerList.length ? 'tips-block--overlay' : 'tips-block--standalone'} font-size-12`}
            onClick={(event) => {
              // 提示词覆盖在 Banner 上方，点击时只应停留在当前页，不能穿透触发 Banner 跳转。
              event.preventDefault();
              event.stopPropagation();
            }}
          />
        )}
      </div>
      {/* 人气推荐页面 */}
      {!!recommendedGoods.length && <PopularityItem list={recommendedGoods} />}
      <div id="contentBlock">
        <Sticky>
          {/* 搜索条件 */}
          <div className="search-toolbar bg-p-tl d-flex-r ai-center p-12" role="search" aria-label="商品筛选">
            {activeProvince && (
              <div className="province-selector d-flex-r ai-center" onClick={() => setprovinceDialogShow(true)}>
                <img className="w-14 h-14 mr-6" src="/pic/icon_address.png" alt="" />
                <span className="province-selector__name text-color-3 font-widget-600">{activeProvince}</span>
              </div>
            )}
            <div className="flex-1 search-ipt d-flex-r ai-center p-8" onClick={() => navigate('/search')}>
              <img className="w-16" src="/pic/icon_search_top.png" alt="" />
              {!!goodsName ? (
                <div className="bw-100 ml-10 d-flex-r ai-center jc-between">
                  <div className="search-value flex-1 mr-10">{goodsName}</div>
                  <img
                    className="w-20 h-20"
                    src="/pic/icon_clear.png"
                    alt=""
                    onClick={(e) => {
                      e.stopPropagation();
                      setGoodsName('');
                      sessionStorage.removeItem('searchIptValue');
                      loadGoodsList();
                    }}
                  />
                </div>
              ) : (
                <span className="search-placeholder ml-10 text-color-3 font-size-12">输入关键字搜索您想要的商品</span>
              )}
            </div>
            <div className="filter-trigger d-flex-r ai-center jc-end" onClick={() => setScreenDialog(true)}>
              <span>筛选</span>
              <img className="w-20 ml-2" src="/pic/icon_more.png" alt="" />
            </div>
          </div>
          {/* 筛选商品类型 */}
          <div className="d-flex-r ai-center goods-tab">
            <GoodsTypeTabBar
              className="flex-1"
              options={goodsTypeData}
              value={goodsType}
              onChange={(v) => {
                if (v === goodsType) return;
                setgoodsType(v);
                setpageNum(1);
                setmobileNet('all');
                loadGoodsList({
                  goodsType: v,
                  pageNum: 1,
                  mobileNet: v === 'equipment' ? null : 'all',
                });
                getRecommendedGoods({ goodsType: v, province: activeProvince || undefined });
              }}
            />
            {styleTrans === '1' && (
              <div
                className="w-75 filter-title d-flex-r ai-center jc-center"
                onClick={() => setStyleLine(styleLine === '0' ? '1' : '0')}
              >
                <img
                  className="w-25"
                  src={`/pic/icon_transformation_${styleLine === '0' ? 'card' : 'list'}.png`}
                  alt=""
                />
              </div>
            )}
          </div>
          {/* 二级筛选 */}
          {goodsType !== 'equipment' && (
            <TabBar
              className="p-12 overflow-x"
              options={goodsType === 'IotCard' ? IotCardOptions : mobileNetOptions}
              value={mobileNet}
              onChange={(v) => {
                if (v === mobileNet) return;
                setmobileNet(v);
                setpageNum(1);
                loadGoodsList({ mobileNet: v, pageNum: 1 });
              }}
            />
          )}
        </Sticky>
        <div className="d-flex-c mb-30">
          {list.length > 0 ? (
            <>
              {styleLine === '0' && (
                <div className="bg-white p-12">
                  {list.map((i, j) => (
                    <GoodsItem pageNum={pageNum} key={i.id} idx={++j} goods={i} />
                  ))}
                </div>
              )}
              {styleLine === '1' && (
                <div className="bg-gray p-10 d-flex-r flex-wrap">
                  {list.map((i, j) => (
                    <GridGoodsItem pageNum={pageNum} key={i.id} idx={++j} goods={i} />
                  ))}
                </div>
              )}
              <Pagination
                value={pageNum}
                onChange={(v) => {
                  handleChangePageNum(v);
                }}
                totalItems={total}
                itemsPerPage={12}
              />
            </>
          ) : (
            <>{errorText ? <Empty /> : <Loading loadingClass="mt-200" tips="正在加载中..." />}</>
          )}
        </div>
      </div>

      {/* 筛选弹窗 */}
      {screenDialog && (
        <ScreenDialog
          mobileNetParam={mobileNet}
          goodsTypeOptions={goodsTypeData}
          goodsTypeParam={goodsType}
          goodsNameParam={goodsName}
          currentProvince={currentProvince}
          onClose={() => setScreenDialog(false)}
          onConfirm={handleConfirmSearch}
        />
      )}
      {/* 省份筛选弹窗 */}
      {provinceDialogShow && (
        <ProvinceDialog
          activeProvince={activeProvince}
          currentProvince={currentProvince}
          onClose={() => setprovinceDialogShow(false)}
          onConfirm={(v) => {
            const selectedProvince = v ?? '';
            setactiveProvince(selectedProvince);
            setpageNum(1);
            setprovinceDialogShow(false);
            loadGoodsList({ province: selectedProvince || undefined, receiverAddress: selectedProvince, pageNum: 1 });
            getRecommendedGoods({ province: selectedProvince || undefined, receiverAddress: selectedProvince });
          }}
        />
      )}

      <LandingCarousel images={h5Config.bottomImgList} className="bottom-swiper" onImageReady={notifyIframeHeight} />
      <FloatingBall
        className="demo-floating-box-bubble"
        offset={{
          right: 0,
          bottom: 80,
        }}
        style={{
          '--rv-floating-box-size': '50px',
        }}
        adsorb={{
          // 滚动缩进比例
          indent: 0.5,
          // 近边停靠距离
          distance: 0,
        }}
      >
        <div
          className="main-button p-5 w-40 h-40 d-flex-r ai-center jc-center bg-white box-shadow zIdx-50"
          onClick={scrollToTop}
        >
          <img className="w-25 h-25" src="/pic/icon_pintop.png" alt="" />
        </div>
      </FloatingBall>
    </Page>
  );
}
